import styled from 'styled-components'
import { useQueryActiveOrders, useQueryOrder } from '@/features/shop/services/Orders/queries'
import { getOrderStatusText } from '@/features/shared/utils/orders'
import { Body } from '@shipt/design-system-typography'
import { NextLink } from '@/features/shared/elements/Link'
import { GridLayoutContainer } from '@/features/shared/styles/layout'
import { ChevronRightIcon } from '@shipt/design-system-icons'
import { OrderStatusIndicator } from '@/features/shop/components/OrderDetails/OrderStatusIndicator'
import { screenSizes } from '@shipt/design-system-themes'
import { useEffect } from 'react'
import { routes } from '@shared/constants/routes'
import { minutesToMilliseconds } from 'date-fns/minutesToMilliseconds'
import { findEarliestActiveOrderToday, isOrderLive } from '@/features/shop/services/Orders/utils'
import { trackElementClicked, trackElementViewed } from '@/features/shared/analytics/element'
import { OrderTimelineMessage } from '@/features/shared/components/HeaderBanner/banners/OrderTimelineMessage'
import { Row, Stack } from '@shipt/design-system-layouts'
import { useOrderETARange } from '@/features/shop/services/OrderEngagement/hooks'
import { type AviatorOrder, type Order } from '@/features/shop/services/Orders/types'

export const LiveOrderStatusBanner = () => {
  const { data: activeOrders = [], refetch: refetchActiveOrders } = useQueryActiveOrders()
  const todaysEarliestOrder = findEarliestActiveOrderToday(activeOrders)

  // We cannot poll active orders because it directly queries the database.
  // We can instead fetch initial data with active orders, then separately
  // poll with useQueryOrder.
  const { data: order } = useQueryOrder(todaysEarliestOrder?.id ?? 0, 'marketplace', {
    enabled: Boolean(todaysEarliestOrder?.id),
    refetchInterval: minutesToMilliseconds(5),
  })

  const shouldRefetchActiveOrders = Boolean(activeOrders.length && order && !isOrderLive(order))

  useEffect(() => {
    if (shouldRefetchActiveOrders) {
      refetchActiveOrders()
    }
  }, [shouldRefetchActiveOrders, refetchActiveOrders])

  const orderForBanner = order ?? todaysEarliestOrder
  if (!orderForBanner) return null

  return <LiveOrderStatusInner order={orderForBanner} />
}

const LiveOrderStatusInner = ({ order }: { order: AviatorOrder | Order }) => {
  const isPickup = order.order_type === 'pickup'
  const { shopperEta } = useOrderETARange({
    orderId: order.id,
    orderStatus: order.status,
    isPickup,
  })

  const {
    status,
    store: { name: storeName },
    time_window,
    id,
    early_delivery = false,
  } = order
  const orderStatusText = getOrderStatusText(status, isPickup)

  useEffect(() => {
    trackElementViewed({
      type: 'banner',
      display_sublocation: 'order_status_banner',
      content: 'order_status',
      order_id: String(order.id),
      order_status: orderStatusText,
    })
  }, [orderStatusText, order.id])

  return (
    <LiveStatusBanner
      href={`${routes.ORDER_DETAIL.url}/${id}`}
      onClick={() =>
        trackElementClicked({
          type: 'banner',
          display_sublocation: 'order_status_banner',
          content: 'order_status',
          order_id: String(order.id),
          order_status: orderStatusText,
        })
      }
    >
      <GridLayoutContainer contentMaxWidth>
        <Row align="center" padding={['lg', 0]}>
          <OrderStatusIndicator data-variant="inverse" />
          <Stack
            aria-label={`${shopperEta} for your ${storeName} order`}
            align="center"
            wrap
            spacing={{ md: 'lg' }}
          >
            <OrderTimelineMessage
              status={status}
              orderId={id}
              isPickup={isPickup}
              timeWindow={time_window}
              isEarlyDelivery={early_delivery}
            />
            <Body surface="inverse">
              {storeName} • {orderStatusText}
            </Body>
          </Stack>
          <ChevronRight inherit />
        </Row>
      </GridLayoutContainer>
    </LiveStatusBanner>
  )
}

const LiveStatusBanner = styled(NextLink)`
  display: block;
  width: 100%;
  background-color: ${({ theme }) => theme.color.background.base.brand.primary};
  color: ${({ theme }) => theme.color.content.base.neutral.primary_inverse};
  text-decoration: none;
`

const ChevronRight = styled(ChevronRightIcon)`
  align-self: start;

  @media ${screenSizes.tablet} {
    display: block;
    margin-left: 2rem;
  }
`
